import { DrawerBody, DrawerFooter, Field, Input, Text } from "@fluentui/react-components";

import LoadingButton from "../../../components/LoadingButton";
import OverlaySpinner from "../../../components/OverlaySpinner";

import { usePutCategoryMutation } from "../api/Update";
import { usePostCategoryMutation } from "../api/Create";
import { useCategoryDrawerStore } from "../store/CategoryDrawerStore";

import useCategoryFormHook from "../hooks/useCategoryFormHook";

import { useUtilitiesContext } from "../../../contexts/UtilitiesContext";
import { useCategoryContext } from "../context/CategoryContext";

import { Category } from "../data/Category";

export default function CategoryForm() {
    const { isEdit, data, close } = useCategoryDrawerStore();
    const { styles, nameId, name, setName } = useCategoryFormHook();
    const { showNotification } = useUtilitiesContext();
    const { refetchCategories } = useCategoryContext();

    const onError = (error: Error) => {
        showNotification({
            type: "error",
            title: "Error",
            message: error.message,
        });
    }

    const postCategoryMutation = usePostCategoryMutation(
        (data: Category) => {
            showNotification({
                type: "success",
                title: "Success",
                message: <Text>A request for approval to create category <b>{data.name}</b> has been sent. Please wait for approval.</Text>,

            });
            close();
            refetchCategories();
        },
        onError
    );

    const putCategoryMutation = usePutCategoryMutation(
        () => {
            showNotification({
                type: "success",
                title: "Success",
                message: <Text>A request for approval to update category <b>{data?.name}</b> has been sent. Please wait for approval.</Text>,
            });
            close();
            refetchCategories();
        },
        onError
    );

    const validateFields = () => {
        const requiredFields = [
            { value: name, message: "Category name is required" },
        ];

        for (const field of requiredFields) {
            if (!field.value) {
                showNotification({
                    type: "error",
                    title: "Error",
                    message: field.message,
                });
                return false;
            }
        }
        return true;
    };

    const onSubmit = () => {
        if (!validateFields()) return;

        if (isEdit && data) {
            putCategoryMutation.mutate({ name: name, id: data.id });
        }
        else {
            postCategoryMutation.mutate(name);
        }
    };

    const isLoading = isEdit ? putCategoryMutation.isPending : postCategoryMutation.isPending;
    const loadingLabel = isEdit ? "Updating..." : "Creating...";
    const buttonText = isEdit ? "Update" : "Create";

    return (
        <>
            <OverlaySpinner open={isLoading} label={loadingLabel} />

            <DrawerBody className={styles.drawerBody}>
                <div className={styles.inputWrapper}>
                    <Field label="Category name" required>
                        <Input
                            id={nameId}
                            type="text"
                            value={name}
                            maxLength={50}
                            onChange={(_, data) => setName(data.value)}
                        />
                    </Field>
                </div>
            </DrawerBody>

            <DrawerFooter>
                <LoadingButton
                    text={buttonText}
                    disabled={isLoading}
                    loading={isLoading}
                    onClick={onSubmit}
                    appearance="primary"
                />
            </DrawerFooter>
        </>
    );
};